import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService implements OnDestroy {
  //private versionUrl = `${environment.TEMPLATE_URI}/version.txt`;
  private readonly versionUrl = './version.txt';
  private readonly cookieKey = 'lios_app_version';
  private intervalId?: number;
  private retryCount = 0;
  private readonly maxRetries = 3;

  constructor(private readonly http: HttpClient) {}

  initVersionCheck(): void {
    // Initial check
    setTimeout(() => this.checkNow(), 1000);
    
    // Periodic check every 5 minutes
    this.intervalId = window.setInterval(() => this.checkNow(), 5 * 60 * 1000);
    
    // Check when user returns to tab
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkNow();
      }
    });
  }

  private checkNow() {
    this.http.get(this.versionUrl, {
      responseType: 'text',
      headers: { 
        'Cache-Control': 'no-cache', 
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    }).subscribe({
      next: version => {
        this.retryCount = 0; // Reset retry count on success
        this.checkVersion(version.trim());
      },
      error: err => {
        console.error('Version check failed:', err);
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => this.checkNow(), 30000); // Retry after 30 seconds
        }
      }
    });
  }

  private async checkVersion(latestVersion: string): Promise<void> {
    const storedVersion = this.getCookie(this.cookieKey);

    if (!storedVersion) {
      this.setCookie(this.cookieKey, latestVersion, 365);
      return;
    }

    if (storedVersion != latestVersion) { // NOSONAR
      console.info(`Version changed (${storedVersion} → ${latestVersion}). Clearing cache...`);
      this.invalidateCache(latestVersion);
    }
  }

  private async invalidateCache(latestVersion: string): Promise<void> {
    try {
      // Clear service worker or cache storage
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Update version cookie
      this.setCookie(this.cookieKey, latestVersion, 365);

      // Force reload (bypass browser cache)
      window.location.reload();
    } catch (e) {
      console.info('Cache invalidation failed:', e);
      window.location.reload();
    }
  }

  /** Helper: Set cookie */
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /** Helper: Get cookie */
  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
} 
